import {Controller} from "@hotwired/stimulus"

// Centered modal context menu shown on long-press of a message/comment bubble.
// Mounted once in application.html.erb (shared/_context_menu.html.erb).
//
// Dispatch to open:
//   document.dispatchEvent(new CustomEvent("context-menu:open", { detail: { src }}))
// Dispatch to close:
//   document.dispatchEvent(new CustomEvent("context-menu:close"))
export default class extends Controller {
    static targets = ["overlay", "panel", "content"]

    connect() {
        this.isOpen = false
        this.isLoading = false

        this._onOpen = this._handleOpen.bind(this)
        this._onClose = this.close.bind(this)
        this._onFrameLoad = this._handleFrameLoad.bind(this)
        this._onKeyDown = this._handleKeyDown.bind(this)
        // Close automatically when any global-drawer or turbo-confirm opens —
        // global-drawer-trigger calls stopPropagation so onPanelClick never fires.
        this._onDrawerOpen = () => this.close()

        document.addEventListener("context-menu:open", this._onOpen)
        document.addEventListener("context-menu:close", this._onClose)
        document.addEventListener("global-drawer:open", this._onDrawerOpen)
        this.element.addEventListener("turbo:frame-load", this._onFrameLoad)
        document.addEventListener("keydown", this._onKeyDown)

        this.element.classList.add("hidden")
    }

    disconnect() {
        document.removeEventListener("context-menu:open", this._onOpen)
        document.removeEventListener("context-menu:close", this._onClose)
        document.removeEventListener("global-drawer:open", this._onDrawerOpen)
        this.element.removeEventListener("turbo:frame-load", this._onFrameLoad)
        document.removeEventListener("keydown", this._onKeyDown)

        if (this.isOpen) {
            document.body.style.overflow = ""
        }
    }

    _handleOpen(event) {
        const {src} = event.detail || {}
        if (!src || !this.hasContentTarget) return
        if (this.isLoading && this.contentTarget.src === src) return

        this.isLoading = true
        this.contentTarget.src = src
    }

    _handleFrameLoad(event) {
        if (event.target !== this.contentTarget) return
        if (!this.isLoading) return
        this.isLoading = false
        this.open()
    }

    _handleKeyDown(event) {
        if (event.key === "Escape" && this.isOpen) this.close()
    }

    // Any action inside the panel closes the menu (after the original handler fires).
    onPanelClick(event) {
        if (event.target.closest("a, button")) {
            setTimeout(() => this.close(), 0)
        }
    }

    open() {
        if (this.isOpen) return

        this.element.classList.remove("hidden")

        requestAnimationFrame(() => {
            if (this.hasOverlayTarget) {
                this.overlayTarget.classList.remove("opacity-0")
                this.overlayTarget.classList.add("opacity-100")
            }
            if (this.hasPanelTarget) {
                this.panelTarget.classList.remove("scale-95", "opacity-0")
                this.panelTarget.classList.add("scale-100", "opacity-100")
            }
            this.element.classList.remove("pointer-events-none")
            this.element.classList.add("pointer-events-auto")
        })

        document.body.style.overflow = "hidden"
        this.isOpen = true
    }

    close() {
        if (!this.isOpen) return

        if (this.hasOverlayTarget) {
            this.overlayTarget.classList.add("opacity-0")
            this.overlayTarget.classList.remove("opacity-100")
        }
        if (this.hasPanelTarget) {
            this.panelTarget.classList.add("scale-95", "opacity-0")
            this.panelTarget.classList.remove("scale-100", "opacity-100")
        }
        this.element.classList.add("pointer-events-none")
        this.element.classList.remove("pointer-events-auto")

        document.body.style.overflow = ""
        this.isOpen = false
        this.isLoading = false

        setTimeout(() => {
            if (!this.isOpen) {
                this.element.classList.add("hidden")
                this._resetContent()
            }
        }, 200)
    }

    _resetContent() {
        if (!this.hasContentTarget) return
        this.contentTarget.innerHTML = `<div class="flex items-center justify-center py-6"></div>`
        this.contentTarget.removeAttribute("src")
    }
}
